'use client';

import { TravelCardCGType, TravelCardPoiCGType } from '@/types/graphql/root';
import { getTravelCard, getTravelCardPoiList, insertSurveyEntryPoint } from '@/utils/actions';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import CloseBlack1414 from '@/svg/main/close_black_14_14.svg';
import ArrowLeftBlack2424 from '@/svg/main/arrow_left_black_24_24.svg';
import Home2424 from '@/svg/main/sideMenu/home_24_24.svg';
import { useParams, usePathname, useRouter } from 'next/navigation';
import TravelCardModalSkeleton from '@/app/ui/main/travel-card-modal-skeleton';
import ImageWithFallback from '@/app/ui/common/image-with-fallback';
import { history } from '@/utils/history';
import ArrowDown106 from '@/svg/trip/arrow_down_10_6.svg';
import { useSetAtom } from 'jotai';
import { historyStackAtom } from '@/atom/historyStack';
import PoiDetailInTravelCard from '@/app/ui/main/poi-detail-in-travel-card';
import usePreventBodyScroll from '@/app/hooks/usePreventBodyScroll';

const INFO_CARD_TITLE_MAP = {
  recommendMonth: '🗓️ 추천 시기',
  flightTime: '✈️ 항공',
  currency: '💵 화폐',
  exchangeRate: '💰 환율',
  timeDifference: '⏰ 시차',
  costOfLiving: '📊 물가',
};

function InfoCard({ title, subValue, value }: { title: string; subValue: string; value: string }) {
  return (
    <div className='flex flex-col gap-12pxr p-12pxr rounded-6pxr border border-new-gray-200 bg-new-gray-100'>
      <span className='body-14-md text-new-gray-800'>{title}</span>
      <div className='flex flex-col gap-2pxr'>
        <span className='body-12-regular text-new-gray-700 min-h-18pxr'>{subValue}</span>
        <span className='sub-title-14-bold text-new-gray-900 min-h-18pxr'>{value}</span>
      </div>
    </div>
  );
}

export default function TravelCardModal({ isIntercepting = true }: { isIntercepting: boolean }) {
  const params = useParams();

  const [isFinishedLoading, setIsFinishedLoading] = useState(false);
  const [travelCard, setTravelCard] = useState<TravelCardCGType | null>(null);
  const [travelCardPois, setTravelCardPois] = useState<TravelCardPoiCGType[]>([]);
  const [isPoiLoading, setIsPoiLoading] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const setHistoryStack = useSetAtom(historyStackAtom);

  usePreventBodyScroll();

  useEffect(() => {
    getTravelCard(params.id as string)
      .then((data) => {
        setTravelCard(data);
        setIsFinishedLoading(true);

        const title = `${data.country} ${data.name} 여행 정보 · 추천 코스 | 찜카 플래너`;
        document.title = title;
      })
      .catch(() => {
        router.push('/not-found');
      });

    setIsPoiLoading(true);

    getTravelCardPoiList(params.id as string)
      .then((data) => {
        setTravelCardPois(data.filter((poi): poi is TravelCardPoiCGType => poi !== null && poi !== undefined));
      })
      .catch(() => {
        console.error('### getTravelCardPoiList error ###');
      })
      .finally(() => {
        setIsPoiLoading(false);
      });
  }, [params.id]);

  useEffect(() => {
    if (descriptionRef.current && travelCard?.description) {
      const element = descriptionRef.current;
      setIsTextTruncated(element.scrollHeight > element.clientHeight || element.scrollHeight > element.offsetHeight);
    }
  }, [travelCard?.description, isFinishedLoading]);

  const onClickStartSurvey = useCallback(async () => {
    try {
      insertSurveyEntryPoint(`TRAVEL-CARD-${travelCard?.name}`);
      if (isIntercepting) {
        history?.go(-1);
      } else {
        router.push('/');
      }

      if (travelCard?.zone) {
        window.sessionStorage.setItem('preparedZoneFromTravelCard', JSON.stringify(travelCard.zone));
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      router.push(`/survey/when`);
    } catch (error) {
      console.error(error);
    }
  }, [isIntercepting, travelCard]);

  const canGoBack = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.history.length > 1;
  }, []);

  const infoCardsWrapperRef = useRef<HTMLDivElement>(null);
  const [isExpandedInfoCards, setIsExpandedInfoCards] = useState(false);
  const infoCards = useMemo(() => {
    if (!travelCard) return [];

    const cardKeys = ['recommendMonth', 'flightTime', 'currency', 'exchangeRate', 'timeDifference', 'costOfLiving'];

    return cardKeys
      .filter((key) => travelCard[key as keyof TravelCardCGType])
      .map((key) => {
        const value = travelCard[key as keyof TravelCardCGType] as string;

        if (key === 'flightTime') {
          const subValue = travelCard.isDirectFlight ? '직항' : '경유';
          const mainValue = travelCard.flightTime;

          return {
            title: INFO_CARD_TITLE_MAP[key as keyof typeof INFO_CARD_TITLE_MAP],
            subValue,
            value: mainValue,
          };
        }

        const [subValue, mainValue] = value.split('|');

        if (!subValue && !mainValue) return null;

        return {
          title: INFO_CARD_TITLE_MAP[key as keyof typeof INFO_CARD_TITLE_MAP],
          subValue: subValue || '',
          value: mainValue || '',
        };
      })
      .filter((infoCard) => infoCard !== null);
  }, [travelCard]);
  const infoCardsHeight = useMemo(() => {
    if (isExpandedInfoCards) {
      if (infoCardsWrapperRef.current?.scrollHeight) {
        return `${infoCardsWrapperRef.current.scrollHeight}px`;
      } else {
        return '100%';
      }
    } else {
      return '96px';
    }
  }, [isExpandedInfoCards, infoCardsWrapperRef.current]);

  const [selectedPoi, setSelectedPoi] = useState<string | null>(null);
  const onClickPoi = useCallback(
    (poiId: string) => {
      history?.push(pathname);
      setHistoryStack((prev) => [
        ...prev,
        {
          name: 'poi-detail 닫기 in TravelCardModal (travelCard/[id]/page.tsx)',
          afterAction: () => {
            setSelectedPoi(null);
          },
        },
      ]);
      setSelectedPoi(poiId);
    },
    [pathname],
  );

  if (!isFinishedLoading) {
    return (
      <div className={`fixed w-full h-dvh max-w-480pxr top-0 z-[21] bg-white ${isIntercepting && 'poi-modal-in-main'}`}>
        <div className='fixed w-full max-w-480pxr top-0 z-[1] h-dvh'>
          <TravelCardModalSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed w-full h-dvh max-w-480pxr top-0 z-[21] bg-white overflow-hidden ${isIntercepting && 'poi-modal-in-main'}`}
    >
      <div className={`w-full max-w-480pxr top-0 z-[1] h-dvh ${selectedPoi && ' travel-card-to-left-25'}`}>
        <div className='w-full max-w-480pxr bg-white h-56pxr py-16pxr px-20pxr flex items-center justify-between'>
          {isIntercepting ? (
            <>
              <span className='w-24pxr h-24pxr'></span>
              <span className='body-16-md text-new-gray-900'>
                {travelCard?.country} {travelCard?.name}
              </span>
              <button className='w-24pxr h-24pxr flex items-center justify-center' onClick={() => router.back()}>
                <CloseBlack1414 />
              </button>
            </>
          ) : (
            <>
              {canGoBack ? (
                <button className='w-24pxr h-24pxr' onClick={() => router.back()}>
                  <ArrowLeftBlack2424 />
                </button>
              ) : (
                <span className='w-24pxr h-24pxr'></span>
              )}
              <span className='body-16-md text-new-gray-900'>
                {travelCard?.country} {travelCard?.name}
              </span>
              <button className='w-24pxr h-24pxr flex items-center justify-center' onClick={() => router.push('/')}>
                <Home2424 />
              </button>
            </>
          )}
        </div>
        <div className='w-full h-[calc(100dvh-56px-98px)] overflow-y-scroll'>
          <ImageWithFallback
            src={travelCard?.mainImageUrl ?? ''}
            alt='travel-card-image'
            width={480}
            height={360}
            quality={100}
            fallbackSrc={travelCard?.mainImageUrl ?? ''}
            className='w-full aspect-[360/200] object-cover'
          />
          <div className='pt-20pxr px-20pxr flex flex-col gap-20pxr'>
            <div className='flex flex-col gap-12pxr'>
              <div className='flex items-center gap-2pxr'>
                <span className='heading-18-bold text-new-gray-900'>
                  {travelCard?.country} {travelCard?.name}
                </span>
                <span className='body-12-bold text-new-gray-700'>{travelCard?.nameEng.toUpperCase()}</span>
              </div>
              <div className='relative'>
                <div
                  ref={descriptionRef}
                  className={`w-full body-14-regular text-new-gray-800 whitespace-pre-line ${showFullText ? '' : 'line-clamp-3'}`}
                >
                  {travelCard?.description}
                  {showFullText && (
                    <button
                      className='text-new-gray-800 body-14-regular underline pl-5pxr'
                      onClick={() => setShowFullText(false)}
                    >
                      접기
                    </button>
                  )}
                </div>
                {isTextTruncated && !showFullText && (
                  <div className='absolute bottom-0 right-0 flex items-center'>
                    <div className='w-30pxr h-20pxr bg-gradient-to-r from-transparent to-white'></div>
                    <button
                      className='text-new-gray-800 body-14-regular underline bg-white pl-2pxr'
                      onClick={() => setShowFullText(true)}
                    >
                      더보기
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className='flex flex-col gap-12pxr'>
              <div
                ref={infoCardsWrapperRef}
                className={`grid grid-cols-3 gap-8pxr transition-all duration-150 overflow-hidden`}
                style={{
                  height: infoCardsHeight,
                }}
              >
                {infoCards.map((card) => (
                  <InfoCard key={card.title} {...card} />
                ))}
              </div>
              {infoCards.length > 3 && (
                <button
                  className='body-12-md text-new-gray-900 w-full h-32pxr rounded-6pxr border border-new-gray-200 bg-white flex items-center justify-center gap-2pxr'
                  onClick={() => setIsExpandedInfoCards(!isExpandedInfoCards)}
                >
                  {isExpandedInfoCards ? '정보 접기' : '정보 더보기'}
                  <span
                    className={`w-16pxr h-16pxr flex items-center justify-center transition-all duration-150 ${
                      isExpandedInfoCards ? 'rotate-180' : ''
                    }`}
                  >
                    <ArrowDown106 />
                  </span>
                </button>
              )}
            </div>
          </div>

          {travelCardPois.length > 0 && (
            <div className='mt-30pxr flex flex-col gap-8pxr'>
              <p className='sub-title-16-bold text-new-gray-900 px-20pxr'>
                {travelCard?.poiTitle && (
                  <>
                    <span className='text-new-point-500'>{travelCard?.poiTitle?.highlightText}</span>
                    {travelCard?.poiTitle?.normalText}
                  </>
                )}
              </p>
              <div className='flex flex-col'>
                {isPoiLoading ? (
                  <div className='w-full'>
                    {[...Array(4)].map((_, index) => (
                      <div
                        key={index}
                        className='w-full h-76pxr border-b border-new-gray-200 px-20pxr py-8pxr flex flex-row gap-12pxr items-center'
                      >
                        <div className='w-60pxr h-60pxr rounded-6pxr skeleton-item'></div>
                        <div className='flex flex-col gap-4pxr flex-1'>
                          <div className='w-1/3 h-18pxr rounded-2pxr skeleton-item'></div>
                          <div className='w-2/3 h-20pxr rounded-2pxr skeleton-item'></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  travelCardPois.map((poi) => (
                    <button
                      key={poi.id}
                      className='border-b border-new-gray-200 px-20pxr py-8pxr flex items-center gap-12pxr pointerhover:hover:bg-new-gray-100'
                      onClick={() => onClickPoi(poi.id)}
                    >
                      <ImageWithFallback
                        src={poi.thumbnail ?? ''}
                        alt={poi.name}
                        width={60}
                        height={60}
                        quality={100}
                        fallbackSrc='https://hoolzzuk-public-images.s3.ap-northeast-2.amazonaws.com/cities/square_128x128.png'
                        className='w-60pxr h-60pxr rounded-6pxr object-cover'
                      />
                      <div className='flex flex-col gap-4pxr'>
                        <span className='sub-title-14-bold text-new-gray-900 text-left line-clamp-1'>{poi.name}</span>
                        <span className='body-14-regular text-new-gray-800 text-left line-clamp-2'>
                          {poi.description || poi.address}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div className='max-w-480pxr w-full bg-white h-98pxr px-20pxr pt-16pxr pb-32pxr'>
          <button
            className='w-full h-50pxr rounded-8pxr bg-new-point-500 text-white body-16-md flex items-center justify-center'
            onClick={() => onClickStartSurvey()}
          >
            {travelCard?.name} 일정표 만들기
          </button>
        </div>
        {selectedPoi && (
          <div className='absolute left-0 top-0 w-[calc(100%-80px)] h-full opacity-50 travel-card-overlay'></div>
        )}
      </div>
      {selectedPoi && <PoiDetailInTravelCard poiId={selectedPoi} />}
    </div>
  );
}
